import { useEffect, useState } from "react";
import api from "../../services/axios";
import "./style.css";

const getDateString = (date) => date.toISOString().split("T")[0];

const formatDateForDisplay = (dateStr) => {
  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`;
};

const generateDateArray = (start, end) => {
  const dates = [];
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    current.setDate(current.getDate() + 1);
    count > 0 && dates.push(new Date(current));
    count++;
  }
  return dates;
};

const getActivityLevel = (count) => {
  if (count === 0) return "level-0";
  if (count < 6) return "level-1";
  if (count < 10) return "level-2";
  if (count < 15) return "level-3";
  return "level-4"; // use darkest green for 10+ calls
};

export default function UserCallHeatmap() {
  const [months, setMonths] = useState([]);
  const [logsMap, setLogsMap] = useState({});
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        const response = await api.post("/getAllCallLogs", {});
        const logs = response.data.data || [];

        const dataByDate = {};
        const logDetailsByDate = {};

        logs.forEach((log) => {
          const dateStr = getDateString(new Date(log.start_stamp));
          if (!dataByDate[dateStr]) {
            dataByDate[dateStr] = {
              count: 0,
              agents: new Set(),
            };
            logDetailsByDate[dateStr] = {};
          }

          dataByDate[dateStr].count += 1;
          dataByDate[dateStr].agents.add(log.answered_agent);

          const agent = log.answered_agent;
          logDetailsByDate[dateStr][agent] =
            (logDetailsByDate[dateStr][agent] || 0) + 1;
        });

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 364);

        const dateArray = generateDateArray(startDate, endDate);

        const finalData = dateArray.map((date) => {
          const dateStr = getDateString(date);
          const data = dataByDate[dateStr];
          return {
            date: dateStr,
            count: data ? data.count : 0,
            agents: data ? Array.from(data.agents) : [],
            isPlaceholder: false,
          };
        });

        const monthsData = {};
        finalData.forEach((day) => {
          const monthKey = day.date.slice(0, 7);
          if (!monthsData[monthKey]) monthsData[monthKey] = [];
          monthsData[monthKey].push(day);
        });

        const monthlyBlocks = Object.keys(monthsData).map((monthKey) => {
          let monthDays = monthsData[monthKey];

          const firstDate = new Date(monthDays[0].date);
          const padStart = (firstDate.getDay() + 6) % 7;

          monthDays = [
            ...Array(padStart).fill({
              date: null,
              count: 0,
              isPlaceholder: true,
              agents: [],
            }),
            ...monthDays,
          ];

          const lastDate = new Date(
            monthDays[monthDays.length - 1].date ?? new Date()
          );
          const padEnd = (7 - lastDate.getDay()) % 7;

          monthDays = [
            ...monthDays,
            ...Array(padEnd).fill({
              date: null,
              count: 0,
              isPlaceholder: true,
              agents: [],
            }),
          ];

          const weeks = [];
          for (let i = 0; i < monthDays.length; i += 7) {
            weeks.push(monthDays.slice(i, i + 7));
          }

          return { month: monthKey, weeks };
        });

        setMonths(monthlyBlocks);
        setLogsMap(logDetailsByDate);
      } catch (err) {
        console.error("Failed to load call logs", err);
      }
    };

    loadLogs();
  }, []);

  const openModal = (day) => {
    if (!day.isPlaceholder) {
      setSelectedDayData(day);
      setShowModal(true);
    }
  };

  const getAgentCallCounts = (dateStr) => {
    return logsMap[dateStr] || {};
  };

  return (
    <div className="salesreportchart">
      <h2 className="heatmap-title">User Call Activity</h2>

      <div className="heatmap-wrapper">
        <div className="heatmap-weekdays">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="weekday-label">
              {day}
            </div>
          ))}
        </div>

        <div className="heatmap-months-wrapper">
          {months.map((monthBlock, monthIndex) => (
            <div key={monthIndex} className="month-block">
              <div className="month-header">
                {new Date(monthBlock.month + "-01").toLocaleString("default", {
                  month: "short",
                  year: "numeric",
                })}
              </div>

              <div className="month-weeks">
                {monthBlock.weeks.map((week, colIndex) => (
                  <div key={colIndex} className="heatmap-column">
                    {week.map((day, rowIndex) => (
                      <div
                        key={`${monthIndex}-${colIndex}-${rowIndex}`}
                        className={`heatmap-cell ${
                          day.isPlaceholder
                            ? "month-placeholder"
                            : getActivityLevel(day.count)
                        }`}
                        onClick={() => openModal(day)}
                      >
                        {!day.isPlaceholder && (
                          <div className="tooltip-content">
                            <div className="status-label">
                              {formatDateForDisplay(day.date)}
                              <br />
                              {day.count} call{day.count !== 1 ? "s" : ""}
                              <br />
                              {day.agents.length > 0 && (
                                <span>
                                  Agent{day.agents.length > 1 ? "s" : ""}:{" "}
                                  {day.agents.join(", ")}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="heatmap-legend">

        <div className="legend-item-title">
          <div className="legend-box level-0"></div> 0 calls
        </div>
        <div className="legend-item-title">
          <div className="legend-box level-1"></div> 1–6 calls
        </div>
        <div className="legend-item-title">
          <div className="legend-box level-2"></div> 7–10 calls
        </div>
        <div className="legend-item-title">
          <div className="legend-box level-3"></div> 11-15 calls
        </div>
        <div className="legend-item-title">
          <div className="legend-box level-4"></div> 15+ calls
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedDayData && (
        <div
          className="modal-overlay leetmodel"
          onClick={() => setShowModal(false)}
        >
          <div className="modal-content " onClick={(e) => e.stopPropagation()}>
            <h3>
              Call Details for {formatDateForDisplay(selectedDayData.date)}
            </h3>
            <ul>
              {Object.entries(getAgentCallCounts(selectedDayData.date)).length >
              0 ? (
                Object.entries(getAgentCallCounts(selectedDayData.date)).map(
                  ([agent, count], idx) => (
                    <li className="modelcount" key={idx}>
                      {agent}: {count} call{count !== 1 ? "s" : ""}
                    </li>
                  )
                )
              ) : (
                <li>No agent activity on this day</li>
              )}
            </ul>
            <button
              className="btn btngradiant"
              onClick={() => setShowModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
